import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
} from "@tanstack/react-query";

import type { Notification } from "@/features/notifications/lib/sentences";
import { api, unwrap, type Schemas } from "@/shared/api/client";
import { makeKeys } from "@/shared/api/queryKeys";
import { useIsLive } from "@/shared/realtime/context";

type NotificationPage = Schemas["NotificationListOut"];
type NotificationPages = InfiniteData<NotificationPage, string | null>;

/** Lists by filter ({unread: true} or {unread: false}); the unread count sits beside them. */
export const notificationKeys = makeKeys("notifications");
export const unreadCountKey = [...notificationKeys.all, "unread-count"] as const;

/* While live updates are off, the badge refreshes this often while the tab is visible, and
   whenever the window regains focus (TanStack Query pauses intervals in background tabs).
   While they are on, a new notification refreshes it the moment it is created. */
export const UNREAD_POLL_MS = 30_000;
const PAGE_SIZE = 20;

export function useUnreadCount() {
  const live = useIsLive();
  return useQuery({
    queryKey: unreadCountKey,
    queryFn: async () => (await unwrap(api.GET("/api/v1/notifications/unread-count"))).unread_count,
    refetchInterval: live ? false : UNREAD_POLL_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    staleTime: 0,
  });
}

/** Notifications, newest first, a page at a time; `unreadOnly` hides the ones already read. */
export function useNotifications(unreadOnly: boolean) {
  const queryClient = useQueryClient();
  return useInfiniteQuery({
    queryKey: notificationKeys.list({ unread: unreadOnly }),
    queryFn: async ({ pageParam }) => {
      const page = await unwrap(
        api.GET("/api/v1/notifications", {
          params: { query: { unread: unreadOnly, cursor: pageParam, limit: PAGE_SIZE } },
        }),
      );
      queryClient.setQueryData(unreadCountKey, page.unread_count);
      return page;
    },
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page.next_cursor,
    staleTime: 0,
  });
}

/** Mark notifications read in every cached list; returns the lists as they were, for rollback. */
function markReadInCache(
  queryClient: QueryClient,
  isAffected: (notification: Notification) => boolean,
) {
  const filters = { queryKey: notificationKeys.lists() };
  const snapshot = queryClient.getQueriesData<NotificationPages>(filters);
  const readAt = new Date().toISOString();
  queryClient.setQueriesData<NotificationPages>(filters, (data) =>
    data
      ? {
          ...data,
          pages: data.pages.map((page) => ({
            ...page,
            notifications: page.notifications.map((notification) =>
              isAffected(notification) && notification.read_at === null
                ? { ...notification, read_at: readAt }
                : notification,
            ),
          })),
        }
      : data,
  );
  return { snapshot, count: queryClient.getQueryData<number>(unreadCountKey) };
}

type ReadSnapshot = ReturnType<typeof markReadInCache>;

function rollBack(queryClient: QueryClient, previous: ReadSnapshot | undefined) {
  if (!previous) return;
  for (const [key, data] of previous.snapshot) queryClient.setQueryData(key, data);
  queryClient.setQueryData(unreadCountKey, previous.count);
}

/** The server's count wins; the unread-only list refetches so read items drop out of it. */
function settle(queryClient: QueryClient, unreadCount: number) {
  queryClient.setQueryData(unreadCountKey, unreadCount);
  void queryClient.invalidateQueries({ queryKey: notificationKeys.list({ unread: true }) });
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (notification: Notification) =>
      unwrap(
        api.POST("/api/v1/notifications/{notification_id}/read", {
          params: { path: { notification_id: notification.id } },
        }),
      ),
    onMutate: async (notification) => {
      await queryClient.cancelQueries({ queryKey: notificationKeys.all });
      const previous = markReadInCache(queryClient, (item) => item.id === notification.id);
      if (notification.read_at === null && previous.count !== undefined) {
        queryClient.setQueryData(unreadCountKey, Math.max(0, previous.count - 1));
      }
      return previous;
    },
    onSuccess: (result) => {
      settle(queryClient, result.unread_count);
    },
    onError: (_error, _notification, previous) => {
      rollBack(queryClient, previous);
    },
  });
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => unwrap(api.POST("/api/v1/notifications/read-all")),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: notificationKeys.all });
      const previous = markReadInCache(queryClient, () => true);
      queryClient.setQueryData(unreadCountKey, 0);
      return previous;
    },
    onSuccess: (result) => {
      settle(queryClient, result.unread_count);
    },
    onError: (_error, _variables, previous) => {
      rollBack(queryClient, previous);
    },
  });
}

/** Opening a task clears its notifications (assigned, mentioned, moved...). */
export function useMarkTaskNotificationsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (taskId: string) =>
      unwrap(
        api.POST("/api/v1/notifications/tasks/{task_id}/read", {
          params: { path: { task_id: taskId } },
        }),
      ),
    onSuccess: (result, taskId) => {
      markReadInCache(queryClient, (notification) => notification.task_id === taskId);
      settle(queryClient, result.unread_count);
    },
  });
}
